import { useEffect, useState } from 'react';
import BulkUploadHeader from '../components/BulkUploadHeader/BulkUploadHeader';
import { useHistory } from 'react-router-dom';
import DownloadOrdersStep0 from './components/DownloadOrdersStep0/DownloadOrdersStep0';
import { ICountryDetail } from 'types/countries';
import DownloadOrdersStep1 from './components/DownloadOrdersStep1/DownloadOrdersStep1';
import { useListCountries } from 'hooks/useListCountries';
import DownloadOrdersStep2 from './components/DownloadOrdersStep2/DownloadOrdersStep2';
import { IUploadResults } from 'types/bulkUpload';
import { uploadOrdersTemplate } from 'services/bulkUpload/uploadOrders.service';
import { AxiosError } from 'axios';
import { DOWNLOAD_ORDERS_STEPS } from './constants';

const DownloadUploadOrdersModule = () => {
  const { push } = useHistory();
  const { countries } = useListCountries();

  const [currentStep, setCurrentStep] = useState(0);
  const [goBackText, setGoBackText] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<ICountryDetail | null>(null);
  const [uploadResults, setUploadResults] = useState<IUploadResults>({
    errors: [],
    updatedRecords: 0,
    totalRecords: 0
  });
  const [errorTemplateURL, setErrorTemplateURL] = useState<string>('');

  const handleBack = () => {
    if (currentStep === 0) {
      push('/bulk-upload/type-of-charge');
    } else {
      setCurrentStep(curr => curr - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < DOWNLOAD_ORDERS_STEPS.length - 1) {
      setCurrentStep(curr => curr + 1);
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      const uploadResult = await uploadOrdersTemplate(file);
      const errors = uploadResult.errors || [];
      const errorsString = errors.flatMap(errorGroup =>
        errorGroup.errors.map(err => (typeof err === 'string' ? err : `Error in row ${err.rowIndex}: ${err.message}`))
      );
      setUploadResults({
        errors: errorsString,
        updatedRecords: uploadResult.createdOrders || 0,
        totalRecords: uploadResult.totalOrders || 0
      });
      setErrorTemplateURL(uploadResult.errorFileUrl || '');
      handleNext();
    } catch (error: AxiosError | any) {
      const message = error.response?.data?.message;
      if (message) {
        const messagesList = typeof message === 'string' ? message.split(',') : message;
        setUploadResults(curr => ({
          ...curr,
          errors: messagesList
        }));
        handleNext();
      }
    }
  };

  const renderComponentInStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DownloadOrdersStep0
            selectedCountry={selectedCountry}
            handleSelectedCountry={value => setSelectedCountry(value)}
            nextStep={handleNext}
            countries={countries || []}
          />
        );
      case 1:
        return (
          <DownloadOrdersStep1
            selectedCountry={selectedCountry}
            handleSelectedCountry={value => setSelectedCountry(value)}
            countries={countries || []}
            handleUploadFile={handleUploadFile}
            templateUrlWithErrors={errorTemplateURL}
          />
        );
      case 2:
        return (
          <DownloadOrdersStep2
            errors={uploadResults?.errors || []}
            updatedRecords={uploadResults?.updatedRecords || 0}
            totalRecords={uploadResults?.totalRecords || 0}
            templateUrl={errorTemplateURL}
            handleBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setGoBackText('Volver a Cargue');
        break;
      case 1:
        setGoBackText(`Volver a ${DOWNLOAD_ORDERS_STEPS[0]}`);
        break;
      case 2:
        setGoBackText(`Volver a ${DOWNLOAD_ORDERS_STEPS[1]}`);
        break;
      default:
        setGoBackText('');
    }
  }, [currentStep]);

  return (
    <main className="downloadUploadCatalogContainer">
      <BulkUploadHeader
        steps={DOWNLOAD_ORDERS_STEPS}
        currentStep={currentStep}
        handleBack={handleBack}
        goBackText={goBackText}
      />
      {renderComponentInStep(currentStep)}
    </main>
  );
};

export default DownloadUploadOrdersModule;
